import { parseBbox, type Bbox } from '../fire-signals/contract';

export const GEO_UI_KEYS = ['geoStyle', 'geoView', 'geoBbox', 'geoFrom', 'geoTo', 'geoLayers'];
export const MAP_PROVIDER = {
  baseUrl: 'https://api.maptiler.com',
  styles: { hybrid: 'hybrid', satellite: 'satellite-v4', streets: 'streets-v4' },
  allowedHosts: ['api.maptiler.com'],
} as const;
export const MAP_STUDY_AREA = {
  id: 'northern-california',
  center: [-121.5, 40] as [number, number],
  bounds: [-124, 38, -119, 42] as Bbox,
  crs: 'EPSG:4326',
  authority: 'VS07 selection-record.json / NASA FIRMS + NOAA NCEI',
};
/** Frozen VS07 selection-record.json / official station-list.txt SHA256.
 * Metadata is not part of the three observation-source API entries. Fail closed
 * on another revision; a future study must supply its separately verified roster.
 */
export const STATION_METADATA = {
  uri: 'https://www.ncei.noaa.gov/oa/global-historical-climatology-network/hourly/doc/ghcnh-station-list.txt',
  revision: 'ff451fafc8f251560029e9ed31ebf0f4d12a2ddd492516c0b166c3744cad182a',
  coordinates: { USW00024257: [-122.2978, 40.5147], USW00023232: [-121.4961, 38.5067] } as Record<
    string,
    readonly number[]
  >,
};
export type Basemap = keyof typeof MAP_PROVIDER.styles;
export type MapFailure =
  | 'MAP_KEY_MISSING'
  | 'MAP_ORIGIN_REJECTED'
  | 'MAP_QUOTA_OR_RATE_LIMIT'
  | 'MAP_STYLE_UNAVAILABLE'
  | 'MAP_OFFLINE'
  | 'WEBGL_UNAVAILABLE';
export function basemap(value: string | null | undefined): Basemap {
  return value === 'satellite' || value === 'streets' ? value : 'hybrid';
}
export function mapStyleUrl(style: Basemap, key: string): string {
  return `${MAP_PROVIDER.baseUrl}/maps/${MAP_PROVIDER.styles[style]}/style.json?key=${encodeURIComponent(key)}`;
}
export function mapFailure(status: number, style = false): MapFailure {
  if (status === 401 || status === 403) return 'MAP_ORIGIN_REJECTED';
  if (status === 429) return 'MAP_QUOTA_OR_RATE_LIMIT';
  return style && status > 0 ? 'MAP_STYLE_UNAVAILABLE' : 'MAP_OFFLINE';
}
export function utc(value: unknown): value is string {
  return (
    typeof value === 'string' &&
    /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{3})?Z$/.test(value) &&
    Number.isFinite(Date.parse(value)) &&
    new Date(value).toISOString().slice(0, 19) === value.slice(0, 19)
  );
}
export function geoScope(search: string) {
  const q = new URLSearchParams(search),
    box = parseBbox(q.get('geoBbox'));
  const from = q.get('geoFrom') ?? '',
    to = q.get('geoTo') ?? '';
  return {
    box,
    from,
    to,
    valid:
      (!q.has('geoBbox') || box !== null) &&
      (!from || utc(from)) &&
      (!to || utc(to)) &&
      (!from || !to || Date.parse(from) < Date.parse(to)) &&
      GEO_UI_KEYS.every((k) => q.getAll(k).length <= 1),
  };
}
export function inBounds(lon: number, lat: number, box: Bbox): boolean {
  return (
    lat >= box[1] &&
    lat <= box[3] &&
    (box[0] < box[2] ? lon >= box[0] && lon <= box[2] : lon >= box[0] || lon <= box[2])
  );
}
export function mapView(value: string | null): [number, number, number, number, number] | null {
  if (!value || value.split(',').some((v) => !v.trim())) return null;
  const v = value.split(',').map(Number);
  if (
    v.length !== 5 ||
    v.some((n) => !Number.isFinite(n)) ||
    Math.abs(v[0]!) > 180 ||
    Math.abs(v[1]!) > 85 ||
    v[2]! < 0 ||
    v[2]! > 20 ||
    Math.abs(v[3]!) > 180 ||
    v[4]! < 0 ||
    v[4]! > 60
  )
    return null;
  return v as [number, number, number, number, number];
}
